import { useEffect, useState, useCallback } from "react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { Package, ChevronRight, Phone, Mail, MapPin, Truck } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = [
  { key: "", label: "Все" },
  { key: "new", label: "Новые" },
  { key: "processing", label: "В работе" },
  { key: "shipped", label: "Отправлены" },
  { key: "delivered", label: "Доставлены" },
  { key: "cancelled", label: "Отменены" },
];

const STATUS_COLOR = {
  new: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20",
  processing: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  shipped: "bg-purple-500/15 text-purple-400 border-purple-500/20",
  delivered: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  cancelled: "bg-[#333]/50 text-[#888] border-[#333]",
};

const STATUS_LABEL = {
  new: "Новый",
  processing: "В работе",
  shipped: "Отправлен",
  delivered: "Доставлен",
  cancelled: "Отменён",
};

const PAYMENT_LABEL = {
  pending: { text: "Ожидает оплаты", color: "text-yellow-400" },
  paid: { text: "Оплачено", color: "text-emerald-400" },
  failed: { text: "Не оплачено", color: "text-red-400" },
  cancelled: { text: "Отменено", color: "text-[#888]" },
};

const formatRub = (n) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(n) + " ₽";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export default function AdminOrders() {
  const { admin } = useAdminAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter
        ? `${API_URL}/api/admin/orders?status=${statusFilter}`
        : `${API_URL}/api/admin/orders`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${admin.token}` } });
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [admin.token, statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateStatus = async (orderNumber, status) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/${encodeURIComponent(orderNumber)}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${admin.token}` },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        const updated = await res.json();
        setSelected(updated);
        fetchOrders();
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-white text-2xl font-sans font-semibold">Orders</h1>
          <p className="text-[#666] text-sm font-sans mt-1">Заказы клиентов</p>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-3 mb-3 scrollbar-hide">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key || "all"}
            onClick={() => setStatusFilter(t.key)}
            className={`px-4 py-2 rounded-full text-xs font-sans whitespace-nowrap border ${
              statusFilter === t.key
                ? "bg-[#D8C08F] text-black border-[#D8C08F]"
                : "bg-transparent text-[#888] border-[#2a2a2a]"
            }`}
            data-testid={`orders-tab-${t.key || "all"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-[#333] border-t-[#D8C08F] rounded-full animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16">
          <Package size={40} className="text-[#333] mx-auto mb-4" />
          <p className="text-[#666] text-sm font-sans">Заказов пока нет</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="orders-list">
          {orders.map((o) => (
            <button
              key={o.id}
              onClick={() => setSelected(o)}
              className="w-full bg-[#1a1a1a] hover:bg-[#222] border border-[#2a2a2a] rounded-xl p-4 flex items-center gap-3 text-left transition-colors"
              data-testid={`order-row-${o.orderNumber}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white text-sm font-sans font-semibold">{o.orderNumber}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-md border ${STATUS_COLOR[o.status]}`}>
                    {STATUS_LABEL[o.status]}
                  </span>
                </div>
                <p className="text-[12px] text-[#888] font-sans truncate">
                  {o.customer.lastName} {o.customer.firstName} · {o.customer.phone}
                </p>
                <p className="text-[11px] text-[#555] font-sans mt-0.5">
                  {formatDate(o.createdAt)} · {o.items.length} {o.items.length === 1 ? "товар" : "товаров"}
                </p>
              </div>
              <div className="text-right shrink-0">
                <div className="text-white text-sm font-sans font-semibold">{formatRub(o.total)}</div>
                <div className={`text-[10px] font-sans ${PAYMENT_LABEL[o.paymentStatus]?.color || "text-[#888]"}`}>
                  {PAYMENT_LABEL[o.paymentStatus]?.text || o.paymentStatus}
                </div>
              </div>
              <ChevronRight size={18} className="text-[#444]" />
            </button>
          ))}
        </div>
      )}

      {/* Drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="order-detail">
          <div
            className="flex-1 bg-black/50"
            onClick={() => setSelected(null)}
            data-testid="order-detail-backdrop"
          />
          <div className="w-full max-w-md bg-[#0f0f0f] border-l border-[#2a2a2a] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white text-lg font-sans font-semibold">{selected.orderNumber}</h2>
              <button onClick={() => setSelected(null)} className="text-[#888] text-2xl leading-none">×</button>
            </div>

            <div className="space-y-3 text-sm">
              <Row label="Создан">{formatDate(selected.createdAt)}</Row>
              <Row label="Оплата" valueClass={PAYMENT_LABEL[selected.paymentStatus]?.color}>
                {PAYMENT_LABEL[selected.paymentStatus]?.text || selected.paymentStatus} · {selected.paymentMethod === "sbp" ? "СБП" : "Карта"}
              </Row>
              <Row label="Статус">
                <select
                  value={selected.status}
                  onChange={(e) => updateStatus(selected.orderNumber, e.target.value)}
                  className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-2 py-1 text-white text-xs"
                  data-testid="order-status-select"
                >
                  {Object.keys(STATUS_LABEL).map((s) => (
                    <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                  ))}
                </select>
              </Row>
            </div>

            <div className="bg-[#1a1a1a] rounded-xl p-3 mt-4">
              <h3 className="text-white text-xs font-sans font-semibold mb-2">Клиент</h3>
              <p className="text-white text-sm font-sans">
                {selected.customer.lastName} {selected.customer.firstName} {selected.customer.middleName || ""}
              </p>
              <div className="flex items-center gap-1.5 text-[#888] text-xs mt-1.5">
                <Phone size={12} /> {selected.customer.phone}
              </div>
              <div className="flex items-center gap-1.5 text-[#888] text-xs mt-1">
                <Mail size={12} /> {selected.customer.email}
              </div>
            </div>

            <div className="bg-[#1a1a1a] rounded-xl p-3 mt-3">
              <h3 className="text-white text-xs font-sans font-semibold mb-2 flex items-center gap-2">
                Доставка <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#D8C08F]/15 text-[#D8C08F]">CDEK</span>
              </h3>
              <div className="flex items-start gap-1.5 text-[#888] text-xs">
                {selected.delivery.method === "pvz" ? <MapPin size={12} className="mt-0.5" /> : <Truck size={12} className="mt-0.5" />}
                <div>
                  <p className="text-white">
                    {selected.delivery.method === "pvz"
                      ? selected.delivery.pvzAddress || "ПВЗ"
                      : `${selected.delivery.city || ""}, ${selected.delivery.address || ""}`}
                  </p>
                  <p className="text-[#666] mt-0.5">
                    {selected.deliveryCost === 0 ? "Бесплатно" : formatRub(selected.deliveryCost)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#1a1a1a] rounded-xl p-3 mt-3">
              <h3 className="text-white text-xs font-sans font-semibold mb-3">Товары</h3>
              <div className="space-y-2.5">
                {selected.items.map((it) => (
                  <div key={`${it.productId}::${it.size || ""}`} className="flex gap-2.5">
                    <div className="w-10 h-12 rounded-lg overflow-hidden bg-[#0a0a0a] shrink-0">
                      {it.image ? (
                        <img src={it.image} alt="" className="w-full h-full object-cover" />
                      ) : it.videoUrl ? (
                        <video src={`${it.videoUrl}#t=0.1`} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                      ) : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-wider text-[#666] font-serif truncate">{it.brand}</p>
                      <p className="text-white text-xs font-sans truncate">{it.productName}</p>
                      <p className="text-[#666] text-[10px]">{it.size && it.size !== "OS" ? `${it.size} · ` : ""}× {it.quantity}</p>
                    </div>
                    <div className="text-white text-xs font-sans font-medium">
                      {formatRub(it.salePrice * it.quantity)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#2a2a2a] mt-3 pt-2 space-y-1 text-xs">
                <div className="flex justify-between text-[#888]"><span>Товары</span><span>{formatRub(selected.itemsTotal)}</span></div>
                <div className="flex justify-between text-[#888]"><span>Доставка</span><span>{selected.deliveryCost === 0 ? "0 ₽" : formatRub(selected.deliveryCost)}</span></div>
                <div className="flex justify-between text-white font-semibold pt-1"><span>Итого</span><span>{formatRub(selected.total)}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children, valueClass = "" }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-[#888] text-xs font-sans">{label}</span>
      <span className={`text-white text-xs font-sans ${valueClass}`}>{children}</span>
    </div>
  );
}
